import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { POI, Tag } from '@poi-planner/shared';
import { useInstanceSettingsStore } from '../store/instanceSettingsStore';
import type { MapBounds } from '../hooks/usePoiFilters';
import {
  DEFAULT_PHOSPHOR_ICON,
  getCustomIconDataUrl,
  getPhosphorRuntimeIconName,
  getPhosphorRuntimeImageId,
  parseTagIcon,
} from '../utils/tagIcons';

interface RouteLine {
  /** Ordered coordinates for the route [lng, lat][] */
  coordinates: [number, number][];
  /** POI IDs in order, for click handling */
  poiIds: string[];
}

interface MapViewProps {
  pois: POI[];
  tags?: Tag[];
  selectedPoiId: string | null;
  /** Increment to force fly-to even if selection hasn't changed */
  flyToTrigger?: number;
  onSelectPoi: (id: string | null) => void;
  /** Called when map viewport changes (debounced) */
  onBoundsChange?: (bounds: MapBounds) => void;
  /** Optional route line connecting POIs in order (for day focus view) */
  routeLine?: RouteLine;
  /** Optional POI order mapping to render route badges on POI markers */
  routeOrderByPoiId?: Map<string, number>;
  /** Optional POIs to fit into view when the parent changes context */
  fitToPois?: POI[];
  fitToPoisKey?: string | number;
}

const SOURCE_ID = 'pois';
const CLUSTER_LAYER = 'clusters';
const CLUSTER_COUNT_LAYER = 'cluster-count';
const UNCLUSTERED_LAYER = 'unclustered-pois';
const POI_SYMBOL_LAYER = 'unclustered-poi-icons';
const ROUTE_POI_SOURCE_ID = 'route-pois';
const ROUTE_POI_CIRCLE_LAYER = 'route-pois-circle';
const ROUTE_POI_SYMBOL_LAYER = 'route-pois-icons';
const ROUTE_POI_ORDER_LAYER = 'route-pois-order';
const ROUTE_SOURCE_ID = 'route-line';
const ROUTE_LAYER_ID = 'route-line-layer';

const DEFAULT_COLOR = '#111418';
const ROUTE_COLOR = '#3b82f6'; // blue-500
const SELECTED_FILL = '#2563eb';
const SELECTED_STROKE = '#60a5fa';
const PHOSPHOR_DUOTONE_BASE_URL = 'https://unpkg.com/@phosphor-icons/core@2.1.1/assets/duotone';
const PHOSPHOR_RUNTIME_ICON_SIZE = 24;
const PHOSPHOR_RUNTIME_ICON_PADDING = 2;
const PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO = 2;
const PHOSPHOR_ICON_FILL_COLOR = '#ffffff';

export function MapView({
  pois,
  tags = [],
  selectedPoiId,
  flyToTrigger,
  onSelectPoi,
  onBoundsChange,
  routeLine,
  routeOrderByPoiId,
  fitToPois,
  fitToPoisKey,
}: MapViewProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const boundsTimeoutRef = useRef<number | null>(null);
  const hasInitialFitRef = useRef(false);
  const lastFlyToPoiRef = useRef<string | null>(null);
  const lastFlyToTriggerRef = useRef<number | undefined>(undefined);
  const mapLoadedRef = useRef(false);
  const resizeFrameRef = useRef<number | null>(null);
  const lastPoiClickTimestampRef = useRef<number>(0);
  const customImageDataRef = useRef<Map<string, string>>(new Map());
  const phosphorImageUrlRef = useRef<Map<string, string>>(new Map());
  const [mapReadyVersion, setMapReadyVersion] = useState(0);

  // Refs to avoid stale closures in map event handlers
  const selectedPoiIdRef = useRef(selectedPoiId);
  const onSelectPoiRef = useRef(onSelectPoi);
  const poiMapRef = useRef<Map<string, POI & { lat: number; lng: number }>>(new Map());
  const fitToPoisRef = useRef<POI[] | undefined>(fitToPois);
  const { settings } = useInstanceSettingsStore();

  // Keep refs in sync
  selectedPoiIdRef.current = selectedPoiId;
  onSelectPoiRef.current = onSelectPoi;
  fitToPoisRef.current = fitToPois;

  const mapboxApiKey = settings?.mapboxApiKey;

  // Build a map of tag name -> tag for quick lookup
  const tagMap = useMemo(() => {
    const map = new Map<string, Tag>();
    for (const tag of tags) {
      map.set(tag.name, tag);
    }
    return map;
  }, [tags]);

  // Build a map of tag id -> tag for quick lookup
  const tagIdMap = useMemo(() => {
    const map = new Map<string, Tag>();
    for (const tag of tags) {
      map.set(tag.id, tag);
    }
    return map;
  }, [tags]);

  // Get the effective tag for a POI
  const getEffectiveTag = useCallback((poi: POI): Tag | null => {
    if (poi.primaryTagId) {
      const tag = tagIdMap.get(poi.primaryTagId);
      if (tag) return tag;
    }
    if (poi.tags?.length > 0) {
      const tag = tagMap.get(poi.tags[0]);
      if (tag) return tag;
    }
    return null;
  }, [tagIdMap, tagMap]);

  // Get the effective tag color for a POI
  const getEffectiveTagColor = useCallback((poi: POI): string => {
    const tag = getEffectiveTag(poi);
    return tag?.color || DEFAULT_COLOR;
  }, [getEffectiveTag]);

  const getEffectiveIconImage = useCallback((poi: POI): string => {
    const tag = getEffectiveTag(poi);
    if (!tag) return getPhosphorRuntimeImageId(DEFAULT_PHOSPHOR_ICON);

    const customDataUrl = getCustomIconDataUrl(tag.icon);
    if (customDataUrl) {
      return `custom-tag-${tag.id}`;
    }

    return getPhosphorRuntimeIconName(tag.icon);
  }, [getEffectiveTag]);

  // Filter POIs that have valid coordinates
  const poisWithCoords = useMemo(() =>
    pois.filter(
      (poi): poi is POI & { lat: number; lng: number } =>
        poi.lat != null && poi.lng != null && !isNaN(poi.lat) && !isNaN(poi.lng)
    ),
    [pois]
  );

  const routePoiEntries = useMemo(() => {
    if (!routeLine) return [];
    const entries: { poiId: string; coord: [number, number] }[] = [];
    for (let i = 0; i < routeLine.poiIds.length; i += 1) {
      const poiId = routeLine.poiIds[i];
      const coord = routeLine.coordinates[i];
      if (poiId && coord) {
        entries.push({ poiId, coord });
      }
    }
    return entries;
  }, [routeLine]);

  const routePoiIdSet = useMemo(() => new Set(routePoiEntries.map((entry) => entry.poiId)), [routePoiEntries]);

  const customTagIcons = useMemo(() => {
    const icons: Array<{ imageId: string; dataUrl: string }> = [];
    for (const tag of tags) {
      const dataUrl = getCustomIconDataUrl(tag.icon);
      if (!dataUrl) continue;
      icons.push({ imageId: `custom-tag-${tag.id}`, dataUrl });
    }
    return icons;
  }, [tags]);

  const usedPhosphorIcons = useMemo(() => {
    const names = new Set<string>([DEFAULT_PHOSPHOR_ICON]);
    for (const poi of poisWithCoords) {
      const tag = getEffectiveTag(poi);
      if (!tag) continue;

      const parsed = parseTagIcon(tag.icon);
      if (parsed.kind === 'phosphor') {
        names.add(parsed.name);
      }
    }
    return Array.from(names);
  }, [poisWithCoords, getEffectiveTag]);

  const phosphorRuntimeIcons = useMemo(() =>
    usedPhosphorIcons.map((name) => ({
      imageId: getPhosphorRuntimeImageId(name),
      url: getPhosphorIconUrl(name),
    })),
    [usedPhosphorIcons]
  );

  // Build POI lookup map
  const poiMap = useMemo(() => {
    const map = new Map<string, POI & { lat: number; lng: number }>();
    for (const poi of poisWithCoords) {
      map.set(poi.id, poi);
    }
    return map;
  }, [poisWithCoords]);

  // Keep poiMap ref in sync for event handlers
  poiMapRef.current = poiMap;

  // Convert POIs to GeoJSON FeatureCollection
  const geojsonData = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: poisWithCoords.map((poi) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [poi.lng, poi.lat],
      },
      properties: {
        id: poi.id,
        name: poi.name,
        tags: poi.tags?.join(', ') || '',
        color: getEffectiveTagColor(poi),
        iconImage: getEffectiveIconImage(poi),
        isSelected: poi.id === selectedPoiId,
        isRoutePoi: routePoiIdSet.has(poi.id),
      },
    })),
  }), [poisWithCoords, getEffectiveTagColor, getEffectiveIconImage, selectedPoiId, routePoiIdSet]);
  const geojsonDataRef = useRef(geojsonData);
  geojsonDataRef.current = geojsonData;

  const routePoiGeojson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: routePoiEntries
      .map((entry, index) => {
        const poi = poiMap.get(entry.poiId);
        if (!poi) return null;

        const routeOrder = routeOrderByPoiId?.get(entry.poiId) ?? (index + 1);
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: entry.coord,
          },
          properties: {
            id: poi.id,
            name: poi.name,
            tags: poi.tags?.join(', ') || '',
            color: getEffectiveTagColor(poi),
            iconImage: getEffectiveIconImage(poi),
            isSelected: poi.id === selectedPoiId,
            routeOrder,
          },
        };
      })
      .filter((feature): feature is NonNullable<typeof feature> => feature !== null),
  }), [routePoiEntries, routeOrderByPoiId, poiMap, getEffectiveTagColor, getEffectiveIconImage, selectedPoiId]);
  const routePoiGeojsonRef = useRef(routePoiGeojson);
  routePoiGeojsonRef.current = routePoiGeojson;

  // Report bounds changes (debounced)
  const reportBounds = useCallback(() => {
    if (!onBoundsChange || !mapRef.current) return;

    if (boundsTimeoutRef.current) {
      window.clearTimeout(boundsTimeoutRef.current);
    }

    boundsTimeoutRef.current = window.setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;

      const bounds = map.getBounds();
      if (!bounds) return;

      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    }, 150);
  }, [onBoundsChange]);

  const fitMapToPois = useCallback((targetPois?: POI[]) => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return false;

    const fitPois = (targetPois ?? fitToPoisRef.current ?? []).filter(
      (poi): poi is POI & { lat: number; lng: number } =>
        poi.lat != null && poi.lng != null && !isNaN(poi.lat) && !isNaN(poi.lng)
    );

    if (fitPois.length === 0) return false;

    if (fitPois.length === 1) {
      const poi = fitPois[0];
      map.easeTo({
        center: [poi.lng, poi.lat],
        zoom: 13,
        duration: 500,
      });
      hasInitialFitRef.current = true;
      return true;
    }

    const bounds = new mapboxgl.LngLatBounds();
    fitPois.forEach((poi) => {
      bounds.extend([poi.lng, poi.lat]);
    });
    map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 500 });
    hasInitialFitRef.current = true;
    return true;
  }, []);

  // Initialize map and add layers
  useEffect(() => {
    if (!mapContainerRef.current || !mapboxApiKey) return;

    mapboxgl.accessToken = mapboxApiKey;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [0, 20],
      zoom: 2,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const scheduleResize = () => {
      if (resizeFrameRef.current != null) return;
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        const currentMap = mapRef.current;
        if (!currentMap) return;
        currentMap.resize();
      });
    };

    // Create popup instance (reused for hover)
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 15,
    });
    popupRef.current = popup;

    map.on('load', () => {
      mapLoadedRef.current = true;
      setMapReadyVersion((v) => v + 1);

      // Add clustered POI source
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: geojsonDataRef.current,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 35,
        clusterMinPoints: 5,
      });

      map.addSource(ROUTE_POI_SOURCE_ID, {
        type: 'geojson',
        data: routePoiGeojsonRef.current,
      });

      // Cluster circles
      map.addLayer({
        id: CLUSTER_LAYER,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': DEFAULT_COLOR,
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18,
            10, 22,
            50, 26,
            100, 30,
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': 'white',
        },
      });

      // Cluster count labels
      map.addLayer({
        id: CLUSTER_COUNT_LAYER,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': 'white',
        },
      });

      // Native Mapbox unclustered POI rendering for stable geolocking.
      map.addLayer({
        id: UNCLUSTERED_LAYER,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'isRoutePoi'], true]],
        paint: {
          'circle-color': ['case', ['==', ['get', 'isSelected'], true], SELECTED_FILL, ['get', 'color']],
          'circle-radius': ['case', ['==', ['get', 'isSelected'], true], 18.5, 14],
          'circle-stroke-width': ['case', ['==', ['get', 'isSelected'], true], 3.25, 2.25],
          'circle-stroke-color': ['case', ['==', ['get', 'isSelected'], true], SELECTED_STROKE, 'white'],
        },
      });

      map.addLayer({
        id: POI_SYMBOL_LAYER,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'isRoutePoi'], true]],
        layout: {
          'icon-image': ['coalesce', ['get', 'iconImage'], getPhosphorRuntimeImageId(DEFAULT_PHOSPHOR_ICON)],
          'icon-size': ['case', ['==', ['get', 'isSelected'], true], 1.18, 1.01],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: {
          'icon-opacity': 1,
        },
      });

      map.addLayer({
        id: ROUTE_POI_CIRCLE_LAYER,
        type: 'circle',
        source: ROUTE_POI_SOURCE_ID,
        paint: {
          'circle-color': ['case', ['==', ['get', 'isSelected'], true], SELECTED_FILL, ['get', 'color']],
          'circle-radius': ['case', ['==', ['get', 'isSelected'], true], 18.5, 14],
          'circle-stroke-width': ['case', ['==', ['get', 'isSelected'], true], 3.25, 2.25],
          'circle-stroke-color': ['case', ['==', ['get', 'isSelected'], true], SELECTED_STROKE, 'white'],
        },
      });

      map.addLayer({
        id: ROUTE_POI_SYMBOL_LAYER,
        type: 'symbol',
        source: ROUTE_POI_SOURCE_ID,
        layout: {
          'icon-image': ['coalesce', ['get', 'iconImage'], getPhosphorRuntimeImageId(DEFAULT_PHOSPHOR_ICON)],
          'icon-size': ['case', ['==', ['get', 'isSelected'], true], 1.18, 1.01],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: {
          'icon-opacity': 1,
        },
      });

      map.addLayer({
        id: ROUTE_POI_ORDER_LAYER,
        type: 'symbol',
        source: ROUTE_POI_SOURCE_ID,
        layout: {
          'text-field': ['to-string', ['get', 'routeOrder']],
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 13,
          'text-offset': [0.9, -0.9],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': 'white',
          'text-halo-color': ROUTE_COLOR,
          'text-halo-width': 8,
        },
      });

      // Click on cluster -> zoom in
      map.on('click', CLUSTER_LAYER, (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER] });
        if (!features.length) return;

        const clusterId = features[0].properties?.cluster_id;
        const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;

        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          const geometry = features[0].geometry;
          if (geometry.type === 'Point') {
            map.easeTo({
              center: geometry.coordinates as [number, number],
              zoom: zoom ?? 14,
            });
          }
        });
      });

      const poiInteractiveLayers = [
        POI_SYMBOL_LAYER,
        UNCLUSTERED_LAYER,
        ROUTE_POI_SYMBOL_LAYER,
        ROUTE_POI_CIRCLE_LAYER,
        ROUTE_POI_ORDER_LAYER,
      ] as const;

      const handlePoiClick = (e: mapboxgl.MapLayerMouseEvent) => {
        const ts = e.originalEvent.timeStamp;
        if (ts === lastPoiClickTimestampRef.current) return;
        lastPoiClickTimestampRef.current = ts;

        const poiId = e.features?.[0]?.properties?.id as string | undefined;
        if (!poiId) return;

        const currentSelected = selectedPoiIdRef.current;
        onSelectPoiRef.current(poiId === currentSelected ? null : poiId);
      };

      const handlePoiHover = (e: mapboxgl.MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        const poiId = e.features?.[0]?.properties?.id as string | undefined;
        if (!poiId) return;

        const poi = poiMapRef.current.get(poiId);
        if (!poi) return;

        const coords =
          feature?.geometry.type === 'Point'
            ? feature.geometry.coordinates as [number, number]
            : [poi.lng, poi.lat] as [number, number];

        popup
          .setLngLat(coords)
          .setHTML(`
            <div style="font-family: system-ui, sans-serif; padding: 4px 0;">
              <div style="font-weight: 600; font-size: 13px; color: #111418;">${poi.name}</div>
              ${poi.tags?.length > 0 ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">${poi.tags.join(', ')}</div>` : ''}
            </div>
          `)
          .addTo(map);
      };

      for (const layerId of poiInteractiveLayers) {
        map.on('click', layerId, handlePoiClick);
        map.on('mouseenter', layerId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mousemove', layerId, handlePoiHover);
        map.on('mouseleave', layerId, () => {
          map.getCanvas().style.cursor = '';
          popup.remove();
        });
      }

      // Cluster hover cursor
      map.on('mouseenter', CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = '';
      });

      // Click on map background -> deselect
      map.on('click', (e) => {
        const hitFeatures = map.queryRenderedFeatures(e.point, {
          layers: [
            CLUSTER_LAYER,
            UNCLUSTERED_LAYER,
            POI_SYMBOL_LAYER,
            ROUTE_POI_CIRCLE_LAYER,
            ROUTE_POI_SYMBOL_LAYER,
            ROUTE_POI_ORDER_LAYER,
          ],
        });
        if (hitFeatures.length > 0) return;
        onSelectPoiRef.current(null);
      });

      // Ensure canvas dimensions match current layout before interaction.
      scheduleResize();
      window.setTimeout(scheduleResize, 0);

      fitMapToPois();

      // Report initial bounds
      reportBounds();
    });

    map.on('moveend', reportBounds);

    const handleWindowResize = () => {
      scheduleResize();
      reportBounds();
    };
    window.addEventListener('resize', handleWindowResize);

    const resizeObserver = typeof ResizeObserver !== 'undefined' && mapContainerRef.current
      ? new ResizeObserver(() => {
          scheduleResize();
          reportBounds();
        })
      : null;

    if (resizeObserver && mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    mapRef.current = map;

    return () => {
      if (boundsTimeoutRef.current) {
        window.clearTimeout(boundsTimeoutRef.current);
      }
      if (resizeFrameRef.current != null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }

      window.removeEventListener('resize', handleWindowResize);
      resizeObserver?.disconnect();

      popup.remove();
      map.remove();

      mapRef.current = null;
      popupRef.current = null;
      mapLoadedRef.current = false;
      customImageDataRef.current.clear();
      phosphorImageUrlRef.current.clear();
      setMapReadyVersion(0);
      hasInitialFitRef.current = false;
      lastFlyToPoiRef.current = null;
      lastFlyToTriggerRef.current = undefined;
    };
  }, [mapboxApiKey, fitMapToPois, reportBounds]);

  // Update POI data when it changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData(geojsonData);
    }

    // Fit bounds on initial load only when no explicit fit target is driving the viewport.
    if (fitToPoisRef.current?.length) return;

    if (poisWithCoords.length > 0 && !selectedPoiId && !hasInitialFitRef.current) {
      const bounds = new mapboxgl.LngLatBounds();
      poisWithCoords.forEach((poi) => {
        bounds.extend([poi.lng, poi.lat]);
      });
      map.fitBounds(bounds, { padding: 60, maxZoom: 14 });
      hasInitialFitRef.current = true;
    }
  }, [geojsonData, poisWithCoords, selectedPoiId]);

  // Update route POI data (non-clustered, geolocked) when route context changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    const source = map.getSource(ROUTE_POI_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData(routePoiGeojson);
    }
  }, [routePoiGeojson]);

  // Keep runtime custom PNG + Phosphor icons in sync with rendered data.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    let cancelled = false;
    const activeCustomIds = new Set(customTagIcons.map((icon) => icon.imageId));
    const activePhosphorIds = new Set(phosphorRuntimeIcons.map((icon) => icon.imageId));

    for (const [imageId] of customImageDataRef.current) {
      if (activeCustomIds.has(imageId)) continue;
      if (map.hasImage(imageId)) {
        map.removeImage(imageId);
      }
      customImageDataRef.current.delete(imageId);
    }

    for (const [imageId] of phosphorImageUrlRef.current) {
      if (activePhosphorIds.has(imageId)) continue;
      if (map.hasImage(imageId)) {
        map.removeImage(imageId);
      }
      phosphorImageUrlRef.current.delete(imageId);
    }

    for (const { imageId, dataUrl } of customTagIcons) {
      const previousData = customImageDataRef.current.get(imageId);
      if (previousData === dataUrl && map.hasImage(imageId)) continue;

      void loadImageFromDataUrl(dataUrl)
        .then((image) => {
          if (cancelled) return;
          if (map.hasImage(imageId)) {
            map.removeImage(imageId);
          }
          map.addImage(imageId, image);
          customImageDataRef.current.set(imageId, dataUrl);
        })
        .catch((err) => {
          console.warn(`Failed to load custom tag icon ${imageId}`, err);
        });
    }

    for (const { imageId, url } of phosphorRuntimeIcons) {
      const previousUrl = phosphorImageUrlRef.current.get(imageId);
      if (previousUrl === url && map.hasImage(imageId)) continue;

      void loadPhosphorIconImageData(url)
        .then((fillImageData) => {
          if (cancelled) return;
          if (map.hasImage(imageId)) {
            map.removeImage(imageId);
          }

          map.addImage(imageId, fillImageData, { pixelRatio: PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO });
          phosphorImageUrlRef.current.set(imageId, url);
        })
        .catch((err) => {
          console.warn(`Failed to load Phosphor icon ${imageId}`, err);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [customTagIcons, phosphorRuntimeIcons, mapReadyVersion]);

  // Fly to selected POI
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedPoiId) {
      lastFlyToPoiRef.current = null;
      return;
    }

    const selectionChanged = selectedPoiId !== lastFlyToPoiRef.current;
    const triggerFired = flyToTrigger !== undefined && flyToTrigger !== lastFlyToTriggerRef.current;

    if (!selectionChanged && !triggerFired) return;

    const selectedPoi = pois.find((p) => p.id === selectedPoiId);
    if (!selectedPoi || selectedPoi.lat == null || selectedPoi.lng == null) return;

    lastFlyToPoiRef.current = selectedPoiId;
    lastFlyToTriggerRef.current = flyToTrigger;
    map.flyTo({
      center: [selectedPoi.lng, selectedPoi.lat],
      zoom: Math.max(map.getZoom(), 14),
      duration: 1000,
    });
  }, [selectedPoiId, flyToTrigger, pois]);

  // Fit map to an explicit POI set when the parent changes context.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitToPois?.length) return;

    map.resize();
    fitMapToPois(fitToPois);
  }, [fitToPois, fitToPoisKey, fitMapToPois]);

  // Manage route line source/layer
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    // Remove existing route layer and source if present
    if (map.getLayer(ROUTE_LAYER_ID)) {
      map.removeLayer(ROUTE_LAYER_ID);
    }
    if (map.getSource(ROUTE_SOURCE_ID)) {
      map.removeSource(ROUTE_SOURCE_ID);
    }

    // If no route line, we're done
    if (!routeLine || routeLine.coordinates.length < 2) return;

    // Add route line source
    map.addSource(ROUTE_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: routeLine.coordinates,
        },
      },
    });

    // Add route line layer (below POI markers)
    map.addLayer({
      id: ROUTE_LAYER_ID,
      type: 'line',
      source: ROUTE_SOURCE_ID,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ROUTE_COLOR,
        'line-width': 3,
        'line-dasharray': [2, 2],
        'line-opacity': 0.7,
      },
    }, CLUSTER_LAYER); // Insert below cluster layer

  }, [routeLine, mapReadyVersion]);

  // No API key configured
  if (!mapboxApiKey) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-100 rounded-xl border border-border">
        <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">map</span>
        <h3 className="text-base font-bold text-text-primary mb-2">Map not configured</h3>
        <p className="text-sm text-text-secondary text-center max-w-xs mb-4">
          Add your Mapbox API key in Settings to display the map.
        </p>
        <a
          href="/settings"
          className="btn-primary text-sm"
        >
          Go to Settings
        </a>
      </div>
    );
  }

  const showEmptyState = poisWithCoords.length === 0;

  return (
    <div
      ref={mapContainerRef}
      className="flex-1 h-full rounded-xl overflow-hidden border border-border"
      style={{ minHeight: '300px', position: 'relative' }}
    >
      {showEmptyState && (
        <div
          className="flex flex-col items-center justify-center bg-slate-100/95"
          style={{ position: 'absolute', inset: 0, zIndex: 10 }}
        >
          <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">location_off</span>
          <h3 className="text-base font-bold text-text-primary mb-2">No locations to display</h3>
          <p className="text-sm text-text-secondary text-center max-w-xs">
            {pois.length === 0
              ? 'Create some POIs to see them on the map.'
              : 'None of your POIs have coordinates yet.'}
          </p>
        </div>
      )}
    </div>
  );
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load data URL image'));
    img.src = dataUrl;
  });
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image URL: ${url}`));
    img.src = url;
  });
}

async function loadPhosphorIconImageData(url: string): Promise<ImageData> {
  const img = await loadImageFromUrl(url);
  return rasterizePhosphorIconImage(img, PHOSPHOR_ICON_FILL_COLOR);
}

function rasterizePhosphorIconImage(img: HTMLImageElement, color: string): ImageData {
  const logicalSize = PHOSPHOR_RUNTIME_ICON_SIZE;
  const pixelRatio = PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO;
  const size = logicalSize * pixelRatio;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2d context for Phosphor icon rasterization');
  }

  const drawSize = (logicalSize - (PHOSPHOR_RUNTIME_ICON_PADDING * 2)) * pixelRatio;
  const scale = Math.min(drawSize / img.naturalWidth, drawSize / img.naturalHeight);
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const x = Math.floor((size - width) / 2);
  const y = Math.floor((size - height) / 2);

  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, x, y, width, height);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, size, size);
  ctx.globalCompositeOperation = 'source-over';

  return ctx.getImageData(0, 0, size, size);
}

function getPhosphorIconUrl(name: string): string {
  return `${PHOSPHOR_DUOTONE_BASE_URL}/${name}-duotone.svg`;
}
